import React, { useState, useEffect } from "react";
import { Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { useOverlapSettings } from "../../react-query/useOverlapSettings";
import { useAuth } from "../../react-query/useAuth";

const OverlapSettings = () => {
  const { userData } = useAuth();
  const { settings, isLoading, isError, updateSettings, isUpdating } =
    useOverlapSettings();

  const [selectedPolicy, setSelectedPolicy] = useState("none");
  const [requireConfirmation, setRequireConfirmation] = useState(true);

  // Initialize state when settings are loaded
  useEffect(() => {
    if (settings?.overlap_policy) {
      setSelectedPolicy(settings.overlap_policy);
    }
    if (settings?.require_confirmation !== undefined) {
      setRequireConfirmation(settings.require_confirmation);
    }
  }, [settings]);

  // Only show to admins
  if (!userData?.is_admin) {
    return null;
  }

  const handlePolicyChange = (e) => {
    setSelectedPolicy(e.target.value);
  };

  const handleSave = () => {
    updateSettings({
      overlap_policy: selectedPolicy,
      require_confirmation: requireConfirmation,
    });
  };

  const policyOptions = [
    {
      value: "none",
      label: "No Overlaps Allowed",
      description:
        "No one can create overlapping classes. The system will block all overlap attempts.",
    },
    {
      value: "admins_only",
      label: "Admins Only",
      description:
        "Only administrators can create overlapping classes (with confirmation dialog).",
    },
    {
      value: "all",
      label: "Allow All (With Confirmation)",
      description:
        "Anyone can create overlapping classes, but must confirm the overlap before saving.",
    },
  ];

  return (
    <Card className="mb-3">
      <Card.Header>
        <h5>Schedule Overlap Policy</h5>
      </Card.Header>
      <Card.Body>
        <Alert variant="info">
          <p className="mb-2">
            <strong>What is overlap policy?</strong>
          </p>
          <p className="mb-0">
            When coaches schedule classes, the system checks if their new class
            overlaps with existing ones. This setting controls who can create
            overlapping schedules and under what conditions.
          </p>
          <hr />
          <p className="mb-0">
            <strong>Recommended setting:</strong> "Admins Only" for controlled
            overlap management while maintaining schedule integrity.
          </p>
        </Alert>

        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading settings...</span>
          </div>
        ) : isError ? (
          <Alert variant="danger">
            Failed to load overlap settings. Please refresh the page.
          </Alert>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Select Overlap Policy</strong>
              </Form.Label>
              {policyOptions.map((option) => (
                <div key={option.value} className="mb-3">
                  <Form.Check
                    type="radio"
                    id={`policy-${option.value}`}
                    name="overlapPolicy"
                    value={option.value}
                    checked={selectedPolicy === option.value}
                    onChange={handlePolicyChange}
                    disabled={isUpdating}
                    label={
                      <div>
                        <strong>{option.label}</strong>
                        <div className="text-muted small">
                          {option.description}
                        </div>
                      </div>
                    }
                  />
                </div>
              ))}
              <Form.Text className="text-muted">
                Current policy:{" "}
                <strong>
                  {policyOptions.find(
                    (opt) => opt.value === settings?.overlap_policy
                  )?.label ||
                    settings?.overlap_policy ||
                    "None"}
                </strong>
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="require-confirmation"
                checked={requireConfirmation}
                onChange={(e) => setRequireConfirmation(e.target.checked)}
                disabled={isUpdating || selectedPolicy === "none"}
                label={
                  <div>
                    <strong>Require Confirmation Dialog</strong>
                    <div className="text-muted small">
                      When enabled, users will see a confirmation dialog before
                      creating overlapping classes. Disable this to allow silent
                      overlap creation for the selected user group.
                    </div>
                  </div>
                }
              />
              <Form.Text className="text-muted">
                {selectedPolicy === "none" && (
                  <span className="text-warning">
                    ⚠️ Confirmation is not applicable when overlaps are blocked
                    for everyone.
                  </span>
                )}
                {selectedPolicy !== "none" && (
                  <>
                    Current setting:{" "}
                    <strong>
                      {settings?.require_confirmation
                        ? "Confirmation required"
                        : "No confirmation (silent)"}
                    </strong>
                  </>
                )}
              </Form.Text>
            </Form.Group>

            <div className="d-grid">
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={
                  isUpdating ||
                  (selectedPolicy === settings?.overlap_policy &&
                    requireConfirmation === settings?.require_confirmation)
                }
              >
                {isUpdating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default OverlapSettings;
